import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useParticipante } from '../../../contexts/ParticipanteContext';

export default function SplashScreen() {
  const navigate = useNavigate();
  const { participante } = useParticipante();
  const [stage, setStage] = useState<'logo' | 'video'>('logo');
  const [progress, setProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Detectar gênero pelo nome (simplificado - em produção usar campo específico)
  const detectarGenero = (nome: string): 'masculino' | 'feminino' => {
    const nomeLower = nome.toLowerCase();
    const terminacoesfemininas = ['a', 'ana', 'ina', 'ela', 'lia', 'ice', 'isa'];
    const ultimasLetras = nomeLower.slice(-3);
    
    for (const terminacao of terminacoesfemininas) {
      if (ultimasLetras.includes(terminacao)) {
        return 'feminino';
      }
    }
    return 'masculino';
  };

  const genero = detectarGenero(participante.nome);
  const sloganBoasVindas = genero === 'feminino' ? 'Seja bem vindo!' : 'Seja bem Vindo';

  useEffect(() => {
    const logoTimer = setTimeout(() => {
      setStage('video');
    }, 2000);

    return () => clearTimeout(logoTimer);
  }, []);

  useEffect(() => {
    if (stage === 'video') {
      const interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + 1;
        });
      }, 100);

      return () => clearInterval(interval);
    }
  }, [stage]);

  const handleSkip = () => {
    navigate('/premiado/home');
  };

  const handleVideoEnd = () => {
    navigate('/premiado/home');
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-orange-500 via-red-500 to-pink-600 overflow-hidden">
      {/* Elementos decorativos de fundo */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-10 w-32 h-32 bg-white rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-32 right-20 w-40 h-40 bg-yellow-300 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-pink-300 rounded-full blur-2xl animate-pulse delay-500"></div>
      </div>

      {/* Logo - aumentado em 20% */}
      <div
        className={`absolute transition-all duration-1000 ease-in-out ${
          stage === 'logo'
            ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 scale-100'
            : 'top-8 left-8 scale-50'
        }`}
      >
        <img
          src="https://static.readdy.ai/image/22e92c18f0b031d861a5e53bf8e362fb/e58d270459b293a355cb2a8d65d2827f.png"
          alt="Get Points Logo"
          className="w-[307px] h-auto drop-shadow-2xl"
        />
      </div>

      {/* Bola com vídeo */}
      <div
        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transition-all duration-1000 ${
          stage === 'video' ? 'opacity-100 scale-100' : 'opacity-0 scale-0'
        }`}
      >
        {/* Anel de progresso animado */}
        <svg className="absolute -inset-4 w-[352px] h-[352px] md:w-[512px] md:h-[512px]" style={{ transform: 'rotate(-90deg)' }}>
          {/* Mobile */}
          <circle
            cx="176"
            cy="176"
            r="168"
            className="md:hidden"
            stroke="rgba(255, 255, 255, 0.2)"
            strokeWidth="8"
            fill="none"
          />
          <circle
            cx="176"
            cy="176"
            r="168"
            className="md:hidden"
            stroke="white"
            strokeWidth="8"
            fill="none"
            strokeDasharray={`${2 * Math.PI * 168}`}
            strokeDashoffset={`${2 * Math.PI * 168 * (1 - progress / 100)}`}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.3s ease' }}
          />
          {/* Desktop */}
          <circle
            cx="256"
            cy="256"
            r="248"
            className="hidden md:block"
            stroke="rgba(255, 255, 255, 0.2)"
            strokeWidth="8"
            fill="none"
          />
          <circle
            cx="256"
            cy="256"
            r="248"
            className="hidden md:block"
            stroke="white"
            strokeWidth="8"
            fill="none"
            strokeDasharray={`${2 * Math.PI * 248}`}
            strokeDashoffset={`${2 * Math.PI * 248 * (1 - progress / 100)}`}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.3s ease' }}
          />
        </svg>

        {/* Container do vídeo */}
        <div className="relative w-[320px] h-[320px] md:w-[480px] md:h-[480px] rounded-full overflow-hidden border-8 border-white shadow-2xl bg-black">
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className="w-full h-full object-cover rounded-full"
            onEnded={handleVideoEnd}
          >
            <source src="https://www.ibanx.com.br/video-institucional.mp4" type="video/mp4" />
          </video>

          {/* Overlay com brilho */}
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent pointer-events-none"></div>
        </div>

        {/* Botão de Som FORA do container do vídeo */}
        <button
          onClick={toggleMute}
          className="absolute bottom-4 right-4 w-14 h-14 bg-white hover:bg-gray-100 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 hover:scale-110 z-50"
          aria-label={isMuted ? 'Ativar som' : 'Desativar som'}
        >
          {isMuted ? (
            <i className="ri-volume-mute-fill text-3xl text-orange-600"></i>
          ) : (
            <i className="ri-volume-up-fill text-3xl text-orange-600"></i>
          )}
        </button>

        {/* Efeito de brilho ao redor */}
        <div className="absolute inset-0 rounded-full bg-white/20 blur-xl animate-pulse pointer-events-none"></div>

        {/* Slogan abaixo do círculo */}
        <div
          className={`absolute top-[calc(100%+2rem)] left-1/2 -translate-x-1/2 whitespace-nowrap transition-all duration-1000 ${
            stage === 'video' ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <h2 className="text-white text-3xl md:text-4xl font-bold drop-shadow-lg">
            {sloganBoasVindas}
          </h2>
        </div>
      </div>

      {/* Botão Pular */}
      <button
        onClick={handleSkip}
        className={`fixed bottom-8 right-8 px-6 py-3 bg-white text-orange-600 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 ${
          stage === 'video' ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        Pular
      </button>

      {/* Indicador de progresso */}
      <div
        className={`fixed bottom-8 left-1/2 -translate-x-1/2 text-white text-sm font-medium ${
          stage === 'video' ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {progress}%
      </div>
    </div>
  );
}
